/**
 * Audit log.
 *
 * Read-only by construction — the API exposes no write endpoint for audit entries, so the record of
 * who changed what cannot be edited by anyone, including a Super Admin.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Badge } from '@/components/ui';
import { DataTable, type Column } from '@/components/admin/DataTable';
import { LoadError } from '@/components/site/blocks';
import { useDebounced } from '@/hooks';
import { cmsApi } from '@/services/api';
import type { AuditEntry } from '@/services/types';

const ACTION_TONES: Record<string, 'success' | 'signal' | 'primary' | 'neutral' | 'danger'> = {
  create: 'primary',
  update: 'neutral',
  delete: 'danger',
  publish: 'success',
  unpublish: 'signal',
  archive: 'neutral',
  submit: 'signal',
  approve: 'success',
  reject: 'signal',
  restore: 'primary',
  login: 'neutral',
  login_failed: 'danger',
  role_change: 'danger',
  settings_change: 'signal',
};

function ChangeSummary({ changes }: { changes: AuditEntry['changes'] }) {
  const entries = Object.entries(changes ?? {});
  if (entries.length === 0) return <span className="text-muted">—</span>;

  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-primary">
        {entries.length} field{entries.length === 1 ? '' : 's'} changed
      </summary>
      <dl className="mt-2 space-y-1">
        {entries.slice(0, 8).map(([field, change]) => (
          <div key={field} className="flex flex-wrap gap-1">
            <dt className="font-medium text-ink">{field}:</dt>
            <dd className="text-muted">
              <span className="line-through">{String(change.from ?? '—').slice(0, 40)}</span>
              {' → '}
              <span className="text-ink">{String(change.to ?? '—').slice(0, 40)}</span>
            </dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

export function AuditPage() {
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounced(search, 300);

  const listQuery = useQuery({
    queryKey: ['cms', 'audit', { debouncedSearch, action, page }],
    queryFn: () =>
      cmsApi.auditLog({ search: debouncedSearch || undefined, action: action || undefined, page, page_size: 30 }),
  });

  const columns: Column<AuditEntry>[] = [
    {
      key: 'created_at',
      header: 'When',
      width: '12rem',
      render: (row) => (
        <span className="text-muted">{new Date(row.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
      ),
    },
    { key: 'actor_label', header: 'Who', width: '12rem', render: (row) => <span className="font-medium text-ink">{row.actor_label}</span> },
    {
      key: 'action',
      header: 'Action',
      width: '10rem',
      render: (row) => <Badge tone={ACTION_TONES[row.action] ?? 'neutral'}>{row.action_label}</Badge>,
    },
    {
      key: 'object_repr',
      header: 'Record',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-ink">{row.object_repr || '—'}</p>
          {row.content_type_label && <p className="text-xs capitalize text-muted">{row.content_type_label}</p>}
          {row.message && <p className="mt-0.5 truncate text-xs italic text-muted">{row.message}</p>}
        </div>
      ),
    },
    { key: 'changes', header: 'Changes', width: '14rem', render: (row) => <ChangeSummary changes={row.changes} /> },
    { key: 'ip_address', header: 'IP', width: '9rem', render: (row) => <span className="font-mono text-xs text-muted">{row.ip_address ?? '—'}</span> },
  ];

  if (listQuery.isError) return <LoadError onRetry={() => void listQuery.refetch()} />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">Audit log</h1>
        <p className="mt-1 text-sm text-muted">Every content change, sign-in, download and permission change.</p>
      </div>

      <Alert tone="info" title="This log cannot be edited">
        Audit entries are append-only. There is no API endpoint that modifies or deletes them, so the record stays
        trustworthy for compliance review.
      </Alert>

      <DataTable
        caption="Audit log"
        columns={columns}
        rows={listQuery.data?.results ?? []}
        count={listQuery.data?.count ?? 0}
        page={listQuery.data?.page ?? 1}
        pages={listQuery.data?.pages ?? 1}
        isLoading={listQuery.isLoading}
        search={search}
        searchPlaceholder="Search by record, user or message…"
        filters={[
          {
            key: 'action',
            label: 'Action',
            options: [
              { value: '', label: 'All actions' },
              { value: 'create', label: 'Created' },
              { value: 'update', label: 'Updated' },
              { value: 'delete', label: 'Deleted' },
              { value: 'publish', label: 'Published' },
              { value: 'unpublish', label: 'Unpublished' },
              { value: 'submit', label: 'Submitted' },
              { value: 'approve', label: 'Approved' },
              { value: 'restore', label: 'Restored' },
              { value: 'login', label: 'Signed in' },
              { value: 'login_failed', label: 'Sign-in failed' },
              { value: 'download', label: 'Downloaded' },
              { value: 'role_change', label: 'Roles changed' },
              { value: 'settings_change', label: 'Settings changed' },
            ],
          },
        ]}
        filterValues={{ action }}
        emptyTitle="No matching audit entries"
        onSearch={(value) => {
          setSearch(value);
          setPage(1);
        }}
        onFilter={(_key, value) => {
          setAction(value);
          setPage(1);
        }}
        onPage={setPage}
      />
    </div>
  );
}
